import { Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IJobEnhancePayload, IJobFieldSettings, getSkillsSuggestions } from 'wisestep-api';
import { WsDialog, WsDialogSize } from 'wisestep-base';

import {
  ISkillOption,
  calculateProgress,
  mapToNormalSkillOptions,
  newSkillOptions,
  transformISkillsToStringArray,
} from '../../helper';
import { useJobEnhancement } from '../../hooks/useJobEnhancement';

import AIEnhanceBar from './AIEnhanceBar';
import JobDescriptionComponent from './JobDescriptionComponent';
import SkillsAutoComplete from './SkillsAutoComplete';
import StarLoader from './StarLoader';

interface JobAIEnhancedFieldsProps {
  jobSkills?: string[] | null;
  savedDescription?: string;
  jobFieldSettings?: IJobFieldSettings;
}

const JobAIEnhancedFields: React.FC<JobAIEnhancedFieldsProps> = ({
  jobSkills,
  jobFieldSettings,
  savedDescription,
}) => {
  const initialSkillList = mapToNormalSkillOptions(jobSkills || []);
  const [skillsOptions, setSkillsOptions] = useState<ISkillOption[]>([]);
  const [skillList, setSkillList] = useState<ISkillOption[]>(initialSkillList);
  const { setValue, getValues } = useFormContext();
  const [descriptionValue, setDescriptionValue] = useState<string>(savedDescription ?? '');
  const [progress, setProgress] = useState<number>(0);
  const { isLoading, isAIEnhanced, enhanceJob } = useJobEnhancement();

  const jobDescription = useWatch({ name: 'description' }) || '';
  const selectedSkills = useWatch({ name: 'skills' }) || [];

  useEffect(() => {
    setProgress(calculateProgress(jobDescription, selectedSkills) || 0);
  }, [jobDescription, selectedSkills]);

  const onSkillInputKey = useCallback(async (skillInput: string) => {
    const result = await getSkillsSuggestions(skillInput);
    if (result.data) {
      setSkillsOptions(mapToNormalSkillOptions(transformISkillsToStringArray(result.data)));
    }
  }, []);

  const onSkillSelection = (skillTags: ISkillOption[]) => {
    setValue('skills', skillTags.map((value) => value?.label) ?? []);
    setSkillList(skillTags);
  };

  const onEnhanceClick = async () => {
    try {
      const payload: IJobEnhancePayload = {
        title: getValues('title') || '',
        locations: getValues('locations') || [],
        description: getValues('description') || '',
        skills: getValues('skills') || [],
        jobType: getValues('jobType') || null,
      };
      const result = await enhanceJob(payload);
      if (result) {
        setDescriptionValue(result?.jobDescription || '');
        const enhancedSkillsList = newSkillOptions(selectedSkills || [], result?.requiredSkills);
        setSkillList(enhancedSkillsList);
        setValue(
          'skills',
          enhancedSkillsList.map((skill) => skill.label)
        );
      }
    } catch (error) {
      console.error('Enhancement failed:', error);
    }
  };

  const showDescription = jobFieldSettings?.description?.hide === false;
  const showSkills = jobFieldSettings?.skills?.hide === false;
  const showEnhanceBar = showDescription || showSkills;

  return isLoading ? (
    <WsDialog openDialog={isLoading} size={WsDialogSize.SQUARE}>
      <StarLoader />
    </WsDialog>
  ) : (
    <>
      {showDescription && (
        <Grid item sm={11.5}>
          <JobDescriptionComponent
            name="description"
            description={descriptionValue ?? ''}
            mandatory={jobFieldSettings?.description?.mandatory}
            showSuccessLabel={isAIEnhanced}
          />
        </Grid>
      )}

      {showSkills && (
        <Grid item sm={11.5}>
          <SkillsAutoComplete
            onValueEnter={onSkillInputKey}
            onChange={onSkillSelection}
            placeholder="Type to Select Skills"
            initialValue={skillList}
            options={skillsOptions}
            name="skills"
            label="Skills"
            showMandatorySymbol={jobFieldSettings.skills?.mandatory}
            enhanceTriggered={isAIEnhanced}
            updatedValue={skillList}
          />
        </Grid>
      )}

      {showEnhanceBar && (
        <Grid item sm={11.5}>
          <AIEnhanceBar
            progress={progress}
            title="Job Quality"
            subtitle="For best results add as much details as you can"
            onEnhanceClick={onEnhanceClick}
            disabled={isAIEnhanced}
            withBackground={true}
          />
        </Grid>
      )}
    </>
  );
};

export default JobAIEnhancedFields;
